import React from 'react';
import ReactDOM from 'react-dom/client';
import './I18n/config';
import '@/styles/index.css';
import App from './App';
import { initializeTenant, migrateToSessionStorage, getTenantId } from './services/tenantService';
import { initializeHostMapping } from './services/hostMappingService';
import { captureUtmParams } from './utils/utmUtils';
import Splash from './Components/Splash';
import { initializeNewRelic } from './observability/newrelic';

initializeNewRelic();

class TenantLoader extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoaded: false,
      error: null
    };
  }

  async componentDidMount() {
    try {
      captureUtmParams();

      // Intercepta query string com token e redireciona para /sso-callback com hash
      // Isso deve acontecer antes de qualquer inicialização
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (token) {
        // Remove o token da query string e redireciona para /sso-callback com hash
        const currentPath = window.location.pathname;
        const newUrl = `${currentPath === '/' ? '' : currentPath}/sso-callback#token=${encodeURIComponent(token)}`;
        window.location.replace(newUrl);
        return; // Não continua a inicialização se houver redirecionamento
      }

      migrateToSessionStorage();
      
      // Inicializa o mapeamento de hosts dinamicamente antes de inicializar o tenant
      await initializeHostMapping();
      
      await initializeTenant();
      this.updateIconsForTenant();
      
      this.setState({ isLoaded: true });
    } catch (error) {
      console.error('Failed to initialize app:', error);
      this.setState({ error: error.message, isLoaded: true });
    }
  }

  updateIconsForTenant() {
    const tenantId = getTenantId();
    
    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
      const iconElements = document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]');
      iconElements.forEach(element => {
        const href = element.getAttribute('href');
        const newHref = href.replace('hosts/prospera/', `${process.env.REACT_APP_S3_BASE_URL}/${tenantId}/`);
        element.setAttribute('href', newHref);
      });
      
      const manifestElement = document.querySelector('link[rel="manifest"]');
      if (manifestElement) {
        manifestElement.setAttribute('href', `/manifest.json`);
      }
    }
  }

  render() {
    const { isLoaded, error } = this.state;

    if (!isLoaded) {
      return <Splash />;
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-screen">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Erro ao carregar o aplicativo</h1>
          <p className="text-gray-700">{error}</p>
          <button 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={() => window.location.reload()}
          >
            Tentar novamente
          </button>
        </div>
      );
    }

    return (
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<TenantLoader />);

// Detecta falha ao carregar scripts/chunks (ex: deploy novo invalida hashes antigos)
// O servidor retorna HTML para o arquivo JS não encontrado → "Unexpected token '<'"
// Recarregar garante que index.html novo e os hashes corretos sejam buscados
window.addEventListener('error', (event) => {
  const { target } = event;
  if (target instanceof HTMLScriptElement || target instanceof HTMLLinkElement) {
    window.location.reload();
  }
}, true);

if ('serviceWorker' in navigator) {
  // Recarrega a página quando um novo SW assume o controle após deploy
  let isRefreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (isRefreshing) return;
    isRefreshing = true;
    window.location.reload();
  });

  window.addEventListener('load', () => {
    // updateViaCache: 'none' ignora o Cache-Control do service-worker.js na checagem de update
    // (o navegador, por padrão, só é obrigado a rechecar ignorando cache 1x a cada 24h — sem isso,
    // um Cache-Control mal configurado no CDN/origem pode manter o SW antigo servindo por horas)
    navigator.serviceWorker.register('/service-worker.js', { scope: '/', updateViaCache: 'none' })
      .then((registration) => {
        // Recheca por uma versão nova sempre que o usuário volta pra aba, em vez de esperar
        // o navegador decidir sozinho (o que pode levar até 24h)
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            registration.update().catch(() => {});
          }
        });
      })
      .catch(error => {
        console.error('Falha no registro do Service Worker:', error);
      });
  });
}
